import { computed, observable, when, makeObservable, actionBound } from "mobx";
import {
    getAssociatedShopPartnershipsByRetailerShopId,
    getPartnershipsByRetailerShopId,
} from "@/services/api/partnership/management";
import PartnershipDTO from "@/dto/partnership/PartnershipDTO";

/**
 * 代理商的关联关系管理【代理商使用】
 */
class RetailerPartnershipsStore {
    constructor() {
        makeObservable(this, {
            establishedPartnershipsMap: observable,
            canBeEstablishedPartnershipsMap: observable,
            establishedPartnershipLoadingMap: observable,
            canBeEstablishedPartnershipLoadingMap: observable,
            supplierPartnershipMap: computed,
            partnershipMap: computed,
            supplierIdToPartnershipsMap: computed,
            loadEstablishedPartnerships: actionBound,
            ensureEstablishedPartnerships: actionBound,
            loadCanBeEstablishedPartnerships: actionBound,
            ensureCanBeEstablishedPartnerships: actionBound,
        });
    }

    /**
     * 已建立的合作关系 ( retailerShopId => PartnershipDTO[])
     */
    establishedPartnershipsMap = new Map<number, PartnershipDTO[]>();

    /**
     * 可建立的潜在合作关系 (关联店铺的合作关系) ( retailerShopId => PartnershipDTO[])
     */
    canBeEstablishedPartnershipsMap = new Map<number, PartnershipDTO[]>();

    /**
     * 加载已建立合作关系接口的Loading
     */
    establishedPartnershipLoadingMap = new Map<number, boolean>();

    /**
     * 加载可建立的合作关系接口的Loading
     */
    canBeEstablishedPartnershipLoadingMap = new Map<number, boolean>();

    /**
     * 供应商与合作关系的映射表 ( supplierShopId => PartnershipDTO)
     * 用于快速查找某个供应商的合作关系
     */
    get supplierPartnershipMap() {
        const mapping = new Map<number, PartnershipDTO>();

        this.establishedPartnershipsMap.forEach((partnerships) => {
            partnerships.forEach((partnership) => {
                const supplierShopId = partnership.shopId;

                mapping.set(supplierShopId, partnership);
            });
        });

        this.canBeEstablishedPartnershipsMap.forEach((partnerships) => {
            partnerships.forEach((partnership) => {
                const supplierShopId = partnership.shopId;

                mapping.set(supplierShopId, partnership);
            });
        });

        return mapping;
    }

    /**
     * 合作关系ID映射表 ( partnershipId => PartnershipDTO )
     * 用于通过ID快速查找对应的合作关系
     */
    get partnershipMap() {
        const mapping = new Map<number, PartnershipDTO>();

        this.establishedPartnershipsMap.forEach((partnerships) => {
            partnerships.forEach((partnership) => {
                mapping.set(partnership.partnershipId, partnership);
            });
        });

        this.canBeEstablishedPartnershipsMap.forEach((partnerships) => {
            partnerships.forEach((partnership) => {
                mapping.set(partnership.partnershipId, partnership);
            });
        });

        return mapping;
    }

    /**
     * 供应商ID到合作关系列表的映射 ( supplerId => PartnershipDTO[] )
     */
    get supplierIdToPartnershipsMap(): Map<number, PartnershipDTO[]> {
        const map = new Map<number, PartnershipDTO[]>();

        // 把已建立的合作关系也算进去
        this.establishedPartnershipsMap.forEach((partnerships) => {
            partnerships.forEach((partnership) => {
                const id = partnership.supplerId!;
                if (!map.has(id)) {
                    map.set(id, []);
                }
                map.get(id)!.push(partnership);
            });
        });

        // 把可建立的潜在合作关系也算进去
        this.canBeEstablishedPartnershipsMap.forEach((partnerships) => {
            partnerships.forEach((partnership) => {
                const id = partnership.supplerId!;
                if (!map.has(id)) {
                    map.set(id, []);
                }
                map.get(id)!.push(partnership);
            });
        });

        return map;
    }

    /**
     * 加载指定店铺的已建立合作关系
     */
    async loadEstablishedPartnerships(retailerShopId: number) {
        try {
            this.establishedPartnershipLoadingMap.set(retailerShopId, true);
            const data = await getPartnershipsByRetailerShopId(retailerShopId);
            this.establishedPartnershipsMap.set(retailerShopId, data);
        } finally {
            this.establishedPartnershipLoadingMap.set(retailerShopId, false);
        }

        return this.establishedPartnershipsMap.get(retailerShopId);
    }

    async ensureEstablishedPartnerships(retailerShopId: number) {
        if (this.establishedPartnershipLoadingMap.get(retailerShopId)) {
            await when(() => !this.establishedPartnershipLoadingMap.get(retailerShopId));
        }

        if (this.establishedPartnershipsMap.has(retailerShopId)) {
            return this.establishedPartnershipsMap.get(retailerShopId);
        }

        return await this.loadEstablishedPartnerships(retailerShopId);
    }

    /**
     * 加载指定店铺的可建立合作关系
     */
    async loadCanBeEstablishedPartnerships(retailerShopId: number) {
        try {
            this.canBeEstablishedPartnershipLoadingMap.set(retailerShopId, true);
            const data = await getAssociatedShopPartnershipsByRetailerShopId(retailerShopId);

            this.canBeEstablishedPartnershipsMap.set(retailerShopId, data);
        } finally {
            this.canBeEstablishedPartnershipLoadingMap.set(retailerShopId, false);
        }

        return this.canBeEstablishedPartnershipsMap.get(retailerShopId) || [];
    }

    async ensureCanBeEstablishedPartnerships(retailerShopId: number) {
        if (this.canBeEstablishedPartnershipLoadingMap.get(retailerShopId)) {
            await when(() => !this.canBeEstablishedPartnershipLoadingMap.get(retailerShopId));
        }

        if (this.canBeEstablishedPartnershipLoadingMap.has(retailerShopId)) {
            return this.canBeEstablishedPartnershipsMap.get(retailerShopId);
        }

        return await this.loadCanBeEstablishedPartnerships(retailerShopId);
    }
}

export default RetailerPartnershipsStore;
